import React, { useState, useRef, useEffect } from 'react'
import { ReactImageTurntable } from 'react-image-turntable'
import { FullContentModal } from './FullContentModal'
import { useProductSlider } from './Sliders/ProductSlider/hooks/use-product-slider'
import { NavButton } from './Sliders/ProductSlider/NavButton'
import { cn } from '@lib/cn'
import {
  productSliderContainerClassName,
  productSliderItemClassName,
} from './Sliders/ProductSlider'
import { Image } from '@components/gassan-ui'
import Product360ViewPlaceholder from './Product360ViewPlaceholder'
import { preloadImage } from '@lib/preload-image'

type Product360ViewProps = {
  images: string[]
  imagesSlider: string[]
  isOpen: boolean
  title: string
  onClose: () => void
}

const Product360View = ({ images, imagesSlider, isOpen, title, onClose }: Product360ViewProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [sliderIndex, setSliderIndex] = useState(0)
  const [isInteracted, setIsInteracted] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const { handlePrev, handleNext } = useProductSlider(
    ref,
    sliderIndex,
    imagesSlider.length + 1,
    (index) => {
      setIsInteracted(false)
      setSliderIndex(index)
    },
  )

  const onCloseModal = () => {
    setIsInteracted(false)
    setSliderIndex(0)
    onClose()
  }

  useEffect(() => {
    if (isOpen && !imagesLoaded) {
      const imagePromises = images.map((src) => preloadImage(src))

      Promise.all(imagePromises)
        .then(() => setImagesLoaded(true))
        .catch((error) => console.error('Failed to load images:', error))
    }
  }, [isOpen, images, imagesLoaded])

  return (
    <FullContentModal open={isOpen} onOpenChange={onCloseModal}>
      <div
        className="relative h-full w-full"
        onMouseDown={() => imagesLoaded && setIsInteracted(true)}
      >
        <NavButton direction="left" onClick={handlePrev} persistent />
        <div className={productSliderContainerClassName} ref={ref}>
          <div
            className={cn(
              productSliderItemClassName,
              imagesLoaded ? 'cursor-grab' : 'cursor-default',
            )}
          >
            {imagesLoaded ? (
              <>
                <ReactImageTurntable images={images} autoRotate={{ disabled: true }} />
                <img
                  src="/images/360-view-hint.png"
                  alt="360 View Hint"
                  className={cn(
                    'pointer-events-none absolute bottom-[85px] left-1/2 w-[65px] -translate-x-1/2 opacity-100',
                    isOpen && !isInteracted && 'animate-move-left-right',
                    isInteracted && 'animate-fade-out',
                  )}
                />
              </>
            ) : (
              <Product360ViewPlaceholder title={title} placeholderImage={images[0]} />
            )}
          </div>
          {imagesSlider.map((src, index) => (
            <div className={productSliderItemClassName} key={src}>
              <Image
                sources={[src]}
                alt={`${title} - ${index + 2}/${imagesSlider.length}`}
                loading="lazy"
                className="absolute h-full w-full"
              />
            </div>
          ))}
        </div>
        <NavButton direction="right" onClick={handleNext} persistent />
      </div>
    </FullContentModal>
  )
}

export default Product360View
